import React from "react";
import {AppearanceSections} from "../layout/Inspector/AppearanceSections";
import {Tags} from "../layout/Tags";
import GradientControls from "../layout/GradientControls";
import {useSetting} from "../contexts/setting";
import {useImages} from "../contexts/images";
import {resolveBannerSelection} from "../data/template-availability";
import {createGenerativeBackgroundPatch} from "./background";
import {type BannerInstance} from "./document";
import {useBannerRuntime, type AssetTarget} from "./BannerRuntime";
import {type BannerChange} from "./scope-adapter";
import {WorkspaceAssetControls} from "./ui/WorkspaceAssetControls";
import {CONTENT_TYPES_WITHOUT_LAYOUTS, CHANNELS_WITHOUT_LAYOUTS} from "../data/constants";
import {Select} from "@rescui/select";
import {CHANNELS} from "../data/products";
import {RELEASES_SETS} from "../data/release-sets";
import LinksPanel from "../layout/LinksPanel";
import {CustomQrControls} from "../layout/Inspector/CustomQrControls";
import {availableTemplates} from "./catalog";

export function BannerInspector({banner, onChange, rootRef, onFormatChange, formatBusy = false}: {
  banner: BannerInstance; onChange: BannerChange; rootRef: React.RefObject<HTMLDivElement>;
  onFormatChange?: (templateKey: string) => void; formatBusy?: boolean;
}) {
  const runtime = useBannerRuntime();
  const settings = useSetting();
  const images = useImages();
  const {definition, product, layouts, layoutOptions} = resolveBannerSelection(banner);
  const change = (patch: Parameters<BannerChange>[1]) => onChange(banner.instanceId, patch);
  const eventLinksCount = banner.appearance.layout >= 1 && banner.appearance.layout <= 3 ? banner.appearance.layout : 1;
  const formats = availableTemplates({channel: definition.channel, product: banner.product, contentType: banner.contentType, set: banner.set})
    .map(({definition: format}) => ({value: format.templateKey, label: `${format.label} (${format.width} × ${format.height})`}));
  const generate = (count = banner.appearance.generativeCount, stops = banner.appearance.generativeStops) => {
    runtime.cancelUpload("background");
    change(createGenerativeBackgroundPatch(banner, count, stops));
  };
  return <>
    <>
      <details className="grid-selection-details"><summary>Template details</summary>
      <span className="label">Channel</span><Select className="select" options={[settings.channel]} value={settings.channel} isDisabled size="m" />
      <span className="label">Product</span><Select className="select" options={[settings.product]} value={settings.product} isDisabled size="m" />
      {CHANNELS.find((c) => c.value === definition.channel).types.filter((t) => t.value !== "action" && Object.hasOwn(t.products, product.code)).length > 1 && <><span className="label">{definition.channel === "releases" ? "Stage" : "Content type"}</span><Select className="select" options={[settings.type]} value={settings.type} isDisabled size="m" /></>}
      {banner.set && <><span className="label">Set</span><Select className="select" options={RELEASES_SETS} value={RELEASES_SETS.find((set) => set.value === banner.set)} isDisabled size="m" /></>}
      </details>
      <div data-grid-format><span className="label" id={`format-${banner.instanceId}`}>Format</span><Select className="select" aria-labelledby={`format-${banner.instanceId}`}
        options={formats} value={formats.find((format) => format.value === banner.templateKey)} size="m"
        isDisabled={!onFormatChange || formatBusy || images.pendingUploadCount > 0 || runtime.backgroundOption !== null || formats.length < 2}
        onChange={(format) => onFormatChange(format.value)} /></div>
      {formatBusy && <p role="status">Changing banner format…</p>}
    </>
    <AppearanceSections channel={definition.channel} layout={runtime.layoutOption ?? banner.appearance.layout} layouts={layoutOptions.length ? layoutOptions : layouts.map((id) => ({id, iconId: id}))}
      showLayout={(!CHANNELS_WITHOUT_LAYOUTS.includes(definition.channel) && !CONTENT_TYPES_WITHOUT_LAYOUTS.includes(banner.contentType) && layouts.length > 1)} onLayout={settings.editLayoutOption}
      fontEnabled={banner.appearance.fontToggle} onFontEnabled={settings.editIsFontToggle} font={banner.appearance.font} onFont={settings.editFontOption}
      typograf={settings.isTypografEnabled} onTypograf={settings.editIsTypografEnabled}
      logoHidden={banner.appearance.logoHidden} onLogoHidden={images.editLogoHidden}
      uppercase={banner.appearance.uppercase} onUppercase={settings.toggleUppercase}
      icon={banner.appearance.icon} onIcon={settings.editIconOption} iconAmount={6} />
    {definition.channel === "events" && <><span className="label">QR Code Links</span><LinksPanel amount={eventLinksCount} /></>}
    <WorkspaceAssetControls banner={banner} onGenerate={generate} />
    {banner.contentType === "new" && <details><summary>Tags</summary><Tags /></details>}
    <GradientControls scopeRef={rootRef} />
    {definition.channel === "events" && <CustomQrControls amount={eventLinksCount} uploadFor={(key) => (event) => {
      const file = event.target.files?.[0];
      if (file) void runtime.upload(key as AssetTarget, file);
      event.target.value = "";
    }} />}
    {runtime.error && <p role="alert">{runtime.error}</p>}
    {images.pendingUploadCount > 0 && <p role="status">Uploading {images.pendingUploadCount} asset(s)…</p>}
  </>;
}
